import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface QuoteQuestion {
  id: number;
  text: string;
  input_type: 'text' | 'select' | 'date' | 'number';
  required: boolean;
  options?: string[];
  placeholder?: string;
  min?: number;
  max?: number;
}

export interface GenerateQuoteQuestionsRequest {
  searchQuery: string;
  vendorNames?: string[];
  propertyAddress?: string;
}

export interface GenerateQuoteQuestionsResponse {
  success: boolean;
  service_type: string;
  questions: QuoteQuestion[];
  total_questions: number;
  query: string;
  address: string;
  service_analysis: {
    original_query: string;
    service_type: string;
    is_urgent: boolean;
  };
  fallback_used: string | null;
  error: string | null;
}

// Constants for query key generation
const METHOD = 'POST';
const ENDPOINT = 'vendor/quote-requirements';

// Generate cache key based on search parameters
export const QUOTE_QUESTIONS_QUERY_KEY = (
  searchQuery: string, 
  address?: string,
  vendorNames?: string[]
) =>
  getQueryKey(METHOD, ENDPOINT, { 
    searchQuery, 
    address, 
    vendorNames: vendorNames?.sort().join(',') // Sort for consistent cache key
  });

// Cached query hook for fetching quote questions
export const useGetQuoteQuestions = (
  searchQuery: string,
  vendorNames?: string[],
  propertyAddress?: string,
  enabled = false
) => {
  const { session } = useAuth();

  return useQuery({
    queryKey: QUOTE_QUESTIONS_QUERY_KEY(searchQuery, propertyAddress, vendorNames),
    queryFn: async () => {
      const data: GenerateQuoteQuestionsRequest = {
        searchQuery,
        vendorNames,
        propertyAddress,
      };
      
      return handleApiCall<GenerateQuoteQuestionsResponse, GenerateQuoteQuestionsRequest>(
        session,
        'POST',
        'vendor/quote-requirements',
        data
      );
    },
    enabled: enabled && !!session?.access_token && !!searchQuery,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes (reduced from 15)
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes (reduced from 30)
    retry: 2,
  });
};

// Legacy mutation hook for backward compatibility
export const useGenerateQuoteQuestions = () => {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (data: GenerateQuoteQuestionsRequest) =>
      handleApiCall<GenerateQuoteQuestionsResponse, GenerateQuoteQuestionsRequest>(
        session,
        'POST',
        'vendor/quote-requirements',
        data
      ),
  });
};

// Hook to invalidate quote questions cache
export const useInvalidateQuoteQuestions = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ 
        queryKey: [METHOD, ENDPOINT],
      });
    },
    invalidateForSearch: (searchQuery: string, address?: string, vendorNames?: string[]) => {
      queryClient.invalidateQueries({ 
        queryKey: QUOTE_QUESTIONS_QUERY_KEY(searchQuery, address, vendorNames),
      });
    },
  };
};